/**
 * useI18n カスタムフック
 *
 * react-i18nextとlanguage-storeを連携させるカスタムフック。
 * language-storeの言語変更を検知してi18nextの言語を自動的に切り替えます。
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '@/stores/language-store';

/**
 * i18nフックの返り値型
 */
export interface UseI18nReturn {
  /** 翻訳関数 */
  t: (key: string, options?: Record<string, unknown>) => string;
  /** 現在の言語 */
  language: 'ja' | 'en';
  /** 言語を変更する関数 */
  changeLanguage: (lang: 'ja' | 'en') => void;
}

/**
 * i18nカスタムフック
 *
 * 使用例:
 * ```tsx
 * const { t, language, changeLanguage } = useI18n();
 * return <h1>{t('common.loading')}</h1>;
 * ```
 */
export function useI18n(): UseI18nReturn {
  const { t, i18n } = useTranslation();
  const { language, setLanguage } = useLanguageStore();

  // language-storeの言語が変更されたらi18nextの言語も変更
  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // 言語変更関数
  const changeLanguage = (lang: 'ja' | 'en') => {
    setLanguage(lang);
  };

  return {
    t,
    language,
    changeLanguage,
  };
}
